/**
 * Theme bootstrap — reads the current host + session, writes
 * <html data-theme="..." data-mode="..."> at boot.
 *
 * Same React bundle serves both products under different hostnames:
 *   mail.postopia.com         → product=postopia
 *   mail.myvigator.com        → product=myvigator
 *   mail-test.postopia.com    → product=postopia (test env)
 *   mail-test.myvigator.com   → product=myvigator (test env)
 *
 * Dark mode preference reads from:
 *   1. localStorage `suite-shell:mode` if set
 *   2. matchMedia('(prefers-color-scheme: dark)') as default
 *
 * Apps call applyThemeOnBoot() once before React mounts.
 */
/**
 * Host → product mapping. Mirrors the locked B1 entry from
 * infra/MAIL-JOINT-DECISIONS-OPEN.md. Add new hosts here when
 * the suite expands.
 */
const HOST_MAP = {
    'mail.postopia.com': { product: 'postopia', apiBase: 'https://api.postopia.com/v1/mail' },
    'mail-test.postopia.com': { product: 'postopia', apiBase: 'https://api-test.postopia.com/v1/mail' },
    'calendar.postopia.com': { product: 'postopia', apiBase: 'https://api.postopia.com/v1/calendar' },
    'mail.myvigator.com': { product: 'myvigator', apiBase: 'https://ems-api.myvigator.com/v1/mail' },
    'mail-test.myvigator.com': { product: 'myvigator', apiBase: 'https://ems-test-api.myvigator.com/v1/mail' },
    'calendar.myvigator.com': { product: 'myvigator', apiBase: 'https://ems-api.myvigator.com/v1/calendar' },
};
/**
 * Resolve the product for the current host. Falls back to a
 * configurable default if unknown — useful for local dev
 * (localhost:5173 → defaults to Postopia).
 */
export function detectProduct(opts = {}) {
    if (typeof window === 'undefined')
        return opts.fallback ?? 'postopia';
    const host = window.location.host.split(':')[0]; // strip port
    if (!host)
        return opts.fallback ?? 'postopia';
    const entry = HOST_MAP[host];
    if (entry)
        return entry.product;
    return opts.fallback ?? 'postopia';
}
/**
 * Resolve the API base for the current host.
 */
export function detectApiBase(opts = {}) {
    if (typeof window === 'undefined')
        return opts.fallback ?? '';
    const host = window.location.host.split(':')[0];
    if (!host)
        return opts.fallback ?? '';
    const entry = HOST_MAP[host];
    if (entry)
        return entry.apiBase;
    // Local dev fallback — same-origin /v1/mail (Vite proxy can route).
    return opts.fallback ?? '/v1/mail';
}
const MODE_STORAGE_KEY = 'suite-shell:mode';
/**
 * Detect the user's preferred mode. Stored choice wins over system.
 */
export function detectMode() {
    if (typeof window === 'undefined')
        return 'light';
    try {
        const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark')
            return stored;
    }
    catch {
        // localStorage can throw in sandboxed iframes; ignore + fall through.
    }
    if (window.matchMedia('(prefers-color-scheme: dark)').matches)
        return 'dark';
    return 'light';
}
/**
 * Write the theme + mode attributes onto <html> so the CSS
 * `:root[data-theme="..."]` blocks activate.
 *
 * Call once before React mounts (in the app's index.tsx).
 */
export function applyTheme(product, mode) {
    if (typeof document === 'undefined')
        return;
    document.documentElement.setAttribute('data-theme', product);
    document.documentElement.setAttribute('data-mode', mode);
    document.documentElement.style.colorScheme = mode;
}
/**
 * Convenience — detect + apply in one call.
 */
export function applyThemeOnBoot(opts = {}) {
    const product = detectProduct(opts);
    const mode = detectMode();
    applyTheme(product, mode);
    return { product, mode };
}
/**
 * Toggle mode + persist + re-apply. For "switch to dark mode" buttons.
 */
export function setMode(mode) {
    if (typeof window === 'undefined')
        return;
    try {
        window.localStorage.setItem(MODE_STORAGE_KEY, mode);
    }
    catch {
        // ignore
    }
    const product = document.documentElement.getAttribute('data-theme') ?? 'postopia';
    applyTheme(product, mode);
}
/**
 * Mobile breakpoint (px) — under this width, layouts switch to
 * single-pane view-switcher patterns (per CLAUDE.md §0
 * "Mobile-first, always"). Matches Tailwind's `md:` breakpoint so
 * future tailwind use stays consistent.
 */
export const MOBILE_BREAKPOINT = 768;
